import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Text, Button, Group, Badge, SimpleGrid } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  FilterDrawer,
  PageHeader,
  StatusBadge,
  TableShell,
  type FilterField,
  type FilterValues
} from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { channelWa } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../lib/types';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useTenantMap } from '../../../data/useTenants';
import { TenantName } from '../../../components/TenantName';
import { CopyableId } from '../../../components/CopyableId';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { RowMenu } from '../../../components/RowMenu';
import { StatCard } from '../../../components/StatCard';
import { useT } from '../../../i18n/I18nProvider';
import { WbaReadiness } from './Readiness';

const MUTED = 'var(--rr-color-text-muted)';
type SignupSession = channelWa.AdminSignupSession;
type SignupStatus = channelWa.SignupStatus;
type SignupStep = channelWa.SignupStep;
type AnyStep = channelWa.SignupStep | channelWa.MoveFromAppStep;

/**
 * The steps in the order the service walks them, so a session's progress
 * renders as a position in a sequence rather than a word on its own. Taken from
 * the same list the customer console shows; a step the service adds later that
 * is not here still renders, just without a position.
 */
const STEPS: SignupStep[] = [
  'launched',
  'meta_returned',
  'token_exchanged',
  'billing_attached',
  'app_subscribed',
  'number_registered',
  'credentials_stored',
  'number_attached'
];

/**
 * The guided move from the WhatsApp Business app walks a different sequence:
 * the person deletes the app account on the phone, the platform waits for
 * Meta to notice, then codes, verifies, registers and attaches. Position is
 * read against the list that matches the row's flow.
 */
const MOVE_STEPS: channelWa.MoveFromAppStep[] = [
  'started',
  'number_on_account',
  'app_deleted',
  'code_requested',
  'number_verified',
  'number_registered',
  'number_attached'
];

/** Coexistence (3 October 2026): the number stays on the WhatsApp Business app, so there is no register step, and its history is asked for last. */
const COEXISTENCE_STEPS: SignupStep[] = [
  'launched', 'meta_returned', 'token_exchanged', 'billing_attached', 'app_subscribed', 'credentials_stored', 'number_attached', 'history_requested'
];

const stepsFor = (flow: channelWa.SignupFlow | undefined): readonly AnyStep[] =>
  flow === 'move_from_app' ? MOVE_STEPS : flow === 'coexistence' ? COEXISTENCE_STEPS : STEPS;

const icon = (path: ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
const CheckIcon = icon(<><circle cx="10" cy="10" r="6.8" /><path d="M7 10.2 9 12.2l4-4.4" /></>);
const HourglassIcon = icon(<><path d="M6 3.5h8M6 16.5h8" /><path d="M6.8 3.5c0 3.2 3.2 4.3 3.2 6.5s-3.2 3.3-3.2 6.5" /><path d="M13.2 3.5c0 3.2-3.2 4.3-3.2 6.5s3.2 3.3 3.2 6.5" /></>);
const AlertIcon = icon(<><path d="M10 3.2 2.8 16h14.4z" /><path d="M10 8v3.4M10 13.6v.1" /></>);
const TenantsIcon = icon(<><circle cx="7.6" cy="7.4" r="2.6" /><path d="M3.2 15.6a4.6 4.6 0 0 1 8.8 0" /><path d="M13.4 5.2a2.6 2.6 0 0 1 0 4.9" /><path d="M14.6 12.2a4.6 4.6 0 0 1 2.4 3.4" /></>);

function toneFor(status: SignupStatus): 'healthy' | 'warning' | 'down' | 'pending' | 'inactive' {
  switch (status) {
    case 'completed': return 'healthy';
    case 'failed': return 'down';
    case 'cancelled': return 'inactive';
    case 'in_progress': return 'pending';
    default: return 'warning';
  }
}

/**
 * Where every customer got to when they connected WhatsApp themselves.
 *
 * This page used to be assembled from the numbers inventory, which could only
 * ever show connections that had already succeeded: precisely not the set that
 * needs help. `signup_sessions` records the attempt from the moment Meta's
 * window opens, transition by transition, cancellations and failures included,
 * so "where did this customer get stuck?" is a row rather than a log search.
 *
 * The default view is therefore unfinished attempts, not all of them: a
 * completed connection is a page nobody opens.
 */
export default function WbaPage() {
  const t = useT();
  const nav = useNavigate();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'signup-sessions'],
    queryFn: () => channelWa.listSignupSessions({ limit: 500 })
  });
  const { map: tenants } = useTenantMap();

  const rows = useMemo(() => (data?.data ?? []) as SignupSession[], [data]);
  const loaded = Boolean(data) && !error;

  const completed = useMemo(() => rows.filter((r) => r.status === 'completed').length, [rows]);
  const failed = useMemo(() => rows.filter((r) => r.status === 'failed').length, [rows]);
  const cancelled = useMemo(() => rows.filter((r) => r.status === 'cancelled').length, [rows]);
  const openAttempts = useMemo(
    () => rows.filter((r) => r.status === 'started' || r.status === 'in_progress').length,
    [rows]
  );
  const stuckTenants = useMemo(
    () => new Set(rows.filter((r) => r.status !== 'completed').map((r) => r.organisation_id)).size,
    [rows]
  );

  // The step attempts stop at most often. This is the whole reason the session
  // record exists, so it gets a line of its own above the table rather than
  // being something an operator has to notice by scrolling.
  const worstStep = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (r.status === 'completed') continue;
      counts.set(r.current_step, (counts.get(r.current_step) ?? 0) + 1);
    }
    let best: { step: string; count: number } | null = null;
    for (const [step, count] of counts) if (!best || count > best.count) best = { step, count };
    return best;
  }, [rows]);

  const [pageSize, setPageSize] = usePersistedState<number>('admin.wba.pageSize', 10);
  const [showCompleted, setShowCompleted] = usePersistedState<boolean>('admin.wba.showCompleted', false);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<SignupSession | null>(null);

  const visible = useMemo(
    () => (showCompleted ? rows : rows.filter((r) => r.status !== 'completed')),
    [rows, showCompleted]
  );

  // Only the tenants, accounts and states that actually appear, so a filter can
  // never select its way to an empty table.
  const filterFields: FilterField[] = useMemo(() => {
    const orgs = Array.from(new Set(visible.map((r) => r.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const statuses = Array.from(new Set(visible.map((r) => r.status)));
    const steps = Array.from(new Set(visible.map((r) => r.current_step)));
    const wabas = Array.from(new Set(visible.map((r) => r.waba_id).filter((w): w is string => Boolean(w)))).sort();
    return [
      {
        key: 'organisation',
        label: t('gateways.numbers.filters.tenant'),
        placeholder: t('gateways.numbers.filters.anyTenant'),
        options: orgs
      },
      {
        key: 'status',
        label: t('gateways.wba.filters.status'),
        placeholder: t('gateways.wba.filters.anyStatus'),
        options: statuses.map((s) => ({ value: s, label: t(`gateways.wba.status.${s}`) }))
      },
      {
        key: 'step',
        label: t('gateways.wba.filters.step'),
        placeholder: t('gateways.wba.filters.anyStep'),
        options: steps.map((s) => ({ value: s, label: t(`gateways.wba.step.${s}`) }))
      },
      {
        key: 'waba',
        label: t('gateways.numbers.columns.waba'),
        placeholder: t('gateways.numbers.filters.anyWaba'),
        options: wabas.map((id) => ({ value: id, label: id }))
      }
    ];
  }, [visible, t, tenants]);

  const hasActiveFilters = Boolean(query.trim()) || Object.values(filters).some(Boolean);
  function clearAllFilters() { setQuery(''); setFilters({}); setPage(1); }

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('gateways.numbers.filters.tenant'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.status) pills.push({ id: 'status', field: t('gateways.wba.filters.status'), value: t(`gateways.wba.status.${filters.status}`) });
  if (filters.step) pills.push({ id: 'step', field: t('gateways.wba.filters.step'), value: t(`gateways.wba.step.${filters.step}`) });
  if (filters.waba) pills.push({ id: 'waba', field: t('gateways.numbers.columns.waba'), value: filters.waba });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notifySuccess(value, t('common.copied'));
    } catch {
      // A denied clipboard is the browser's decision to report, not ours.
    }
  }

  const filtered = useMemo(() => {
    let list = visible;
    if (filters.organisation) list = list.filter((r) => r.organisation_id === filters.organisation);
    if (filters.status) list = list.filter((r) => r.status === filters.status);
    if (filters.step) list = list.filter((r) => r.current_step === filters.step);
    if (filters.waba) list = list.filter((r) => r.waba_id === filters.waba);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((r) =>
      (r.msisdn ?? '').toLowerCase().includes(q) ||
      (r.display_name ?? '').toLowerCase().includes(q) ||
      (r.waba_id ?? '').toLowerCase().includes(q) ||
      (r.phone_number_id ?? '').toLowerCase().includes(q) ||
      (r.error_code ?? '').toLowerCase().includes(q) ||
      (r.error_message ?? '').toLowerCase().includes(q) ||
      (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q)
    );
    return list;
  }, [visible, filters, query, tenants]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('gateways.title')}
        title={t('gateways.wba.title')}
        description={t('gateways.wba.pageDescription')}
        actions={
          <Group gap="sm" wrap="nowrap">
            <Button
              size="xs"
              variant={showCompleted ? 'filled' : 'outline'}
              onClick={() => { setShowCompleted(!showCompleted); setPage(1); }}
            >
              {showCompleted ? t('gateways.wba.hideCompleted') : t('gateways.wba.showCompleted')}
            </Button>
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
          </Group>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          loading={isLoading}
          tone="success"
          icon={CheckIcon}
          label={t('gateways.wba.metrics.completed')}
          value={loaded ? formatInteger(completed) : t('common.notAvailable')}
          hint={loaded && rows.length > 0
            ? t('gateways.wba.metrics.completedHint', { pct: ((completed / rows.length) * 100).toFixed(1) })
            : undefined}
        />
        <StatCard
          loading={isLoading}
          tone="warning"
          icon={HourglassIcon}
          label={t('gateways.wba.metrics.open')}
          value={loaded ? formatInteger(openAttempts) : t('common.notAvailable')}
          hint={t('gateways.wba.metrics.openHint')}
        />
        <StatCard
          loading={isLoading}
          tone="error"
          icon={AlertIcon}
          label={t('gateways.wba.metrics.failed')}
          value={loaded ? formatInteger(failed) : t('common.notAvailable')}
          hint={loaded ? t('gateways.wba.metrics.failedHint', { cancelled: formatInteger(cancelled) }) : undefined}
        />
        <StatCard
          loading={isLoading}
          tone="info"
          icon={TenantsIcon}
          label={t('gateways.wba.metrics.tenants')}
          value={loaded ? formatInteger(stuckTenants) : t('common.notAvailable')}
          hint={t('gateways.wba.metrics.tenantsHint')}
        />
      </SimpleGrid>

      {/* Whether the accounts actually work, asked of Meta rather than read
          from our own tables. Above the attempt log on purpose: an operator
          opening this page during an incident needs "is it broken and where"
          before "who got stuck last Tuesday". */}
      <WbaReadiness />

      {/* Where attempts stop. One sentence, because it is the answer the page
          was built to give and nobody should have to derive it from a table. */}
      {loaded && worstStep && worstStep.count > 0 && (
        <Alert color="info" title={t('gateways.wba.dropOff.title')}>
          {t('gateways.wba.dropOff.body', {
            step: t(`gateways.wba.step.${worstStep.step}`),
            count: formatInteger(worstStep.count)
          })}
        </Alert>
      )}

      <TableShell
        title={showCompleted ? t('gateways.wba.table.allTitle') : t('gateways.wba.table.title')}
        description={t('gateways.wba.table.description')}
        totalLabel={t('gateways.wba.table.totalLabel', {
          shown: formatInteger(filtered.length),
          total: formatInteger(visible.length)
        })}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('gateways.wba.table.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        belowToolbar={<ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={hasActiveFilters ? t('gateways.numbers.empty') : t('gateways.wba.empty.title')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('gateways.wba.empty.description')}
            action={hasActiveFilters
              ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button>
              : <Button size="xs" variant="outline" onClick={() => nav('/gateways/numbers')}>{t('gateways.wba.interim.openNumbers')}</Button>}
          />
        }
        error={error ? (
          <Alert color="error" title={t('gateways.wba.loadError')}>
            <Stack gap="sm" align="flex-start">
              <Text role="bodySmall">{(error as Error).message}</Text>
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Stack>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        <DataTable<Indexed<SignupSession>>
          records={paged as Indexed<SignupSession>[]}
          loading={isLoading}
          idAccessor="id"
          emptyState={t('gateways.wba.empty.title')}
          onRowClick={(r) => setOpen(r)}
          columns={[
            {
              accessor: 'organisation_id',
              title: t('gateways.numbers.columns.tenant'),
              render: (r) => <TenantName id={r.organisation_id} />
            },
            {
              accessor: 'status',
              title: t('gateways.wba.columns.status'),
              render: (r) => (
                <StatusBadge status={toneFor(r.status)}>{t(`gateways.wba.status.${r.status}`)}</StatusBadge>
              )
            },
            {
              // Which step, and how far along the sequence it is. "Step 3 of 7"
              // is the difference between "they gave up at the start" and "they
              // got all the way to the last call".
              accessor: 'current_step',
              title: t('gateways.wba.columns.step'),
              render: (r) => {
                const steps = stepsFor(r.flow);
                const at = steps.indexOf(r.current_step);
                return (
                  <TwoLineCell
                    primary={t(`gateways.wba.step.${r.current_step}`)}
                    secondary={at >= 0
                      ? t('gateways.wba.stepPosition', { at: at + 1, total: steps.length })
                      : undefined}
                  />
                );
              }
            },
            {
              accessor: 'msisdn',
              title: t('gateways.numbers.columns.number'),
              render: (r) => r.msisdn || r.display_name
                ? <TwoLineCell primary={r.msisdn ?? t('gateways.numbers.unnamed')} secondary={r.display_name ?? undefined} />
                // Meta names the number only once it has been read back, so an
                // attempt that failed before that has none. Saying so beats an
                // empty cell that reads like missing data.
                : <Text role="caption" c={MUTED}>{t('gateways.wba.numberNotChosen')}</Text>
            },
            {
              accessor: 'waba_id',
              title: t('gateways.numbers.columns.waba'),
              render: (r) => r.waba_id
                ? <CopyableId value={r.waba_id} label={t('gateways.numbers.copyWaba')} chars={12} />
                : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
            },
            {
              // Meta's own word for the number, which is not the same as ours:
              // a session can be `completed` on our side with Meta still saying
              // PENDING, and that gap is exactly what support is asked about.
              accessor: 'meta_status',
              title: t('gateways.wba.columns.metaStatus'),
              render: (r) => r.meta_status
                ? <Badge size="sm" variant="light">{r.meta_status}</Badge>
                : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
            },
            {
              accessor: 'error_code',
              title: t('gateways.wba.columns.problem'),
              render: (r) => r.error_code
                ? (
                  <Tooltip label={r.error_message ?? r.error_code} position="top">
                    <Badge size="sm" variant="light" color="red">{r.error_code}</Badge>
                  </Tooltip>
                )
                : <Text role="caption" c={MUTED}>{t('common.none')}</Text>
            },
            {
              accessor: 'started_at',
              title: t('gateways.wba.columns.started'),
              sortable: true,
              render: (r) => <span title={formatDateTime(r.started_at)}>{formatRelative(r.started_at)}</span>
            },
            {
              accessor: 'actions',
              title: t('common.actions'),
              width: 64,
              textAlign: 'right',
              render: (r) => (
                <RowMenu
                  actions={[
                    { id: 'open', label: t('gateways.wba.actions.openSession'), onSelect: () => setOpen(r) },
                    { id: 'tenant', label: t('gateways.numbers.actions.openTenant'), onSelect: () => nav(`/tenants/${r.organisation_id}`) },
                    { id: 'numbers', label: t('gateways.wba.interim.openNumbers'), onSelect: () => nav('/gateways/numbers') },
                    ...(r.waba_id ? [{ id: 'waba', label: t('gateways.numbers.copyWaba'), onSelect: () => void copy(r.waba_id as string) }] : []),
                    ...(r.phone_number_id ? [{ id: 'metaId', label: t('gateways.numbers.copyMetaId'), onSelect: () => void copy(r.phone_number_id as string) }] : [])
                  ]}
                />
              )
            }
          ]}
        />
      </TableShell>

      {/* The transition log, which is the record itself rather than a summary
          of it: every step the attempt reached, with the time it reached it, so
          a support answer is a read rather than a reconstruction. */}
      <Drawer
        opened={open !== null}
        onClose={() => setOpen(null)}
        title={t('gateways.wba.drawer.title')}
        position="right"
        size="lg"
      >
        {open && (
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <TenantName id={open.organisation_id} />
              <StatusBadge status={toneFor(open.status)}>{t(`gateways.wba.status.${open.status}`)}</StatusBadge>
            </Group>

            <Stack gap="xs">
              <Field label={t('gateways.wba.columns.step')} value={t(`gateways.wba.step.${open.current_step}`)} />
              <Field label={t('gateways.numbers.columns.waba')} value={open.waba_id ?? t('common.notAvailable')} />
              <Field label={t('gateways.numbers.columns.metaId')} value={open.phone_number_id ?? t('common.notAvailable')} />
              <Field label={t('gateways.numbers.columns.number')} value={open.msisdn ?? t('gateways.wba.numberNotChosen')} />
              <Field label={t('gateways.wba.drawer.displayName')} value={open.display_name ?? t('common.notAvailable')} />
              <Field label={t('gateways.wba.columns.metaStatus')} value={open.meta_status ?? t('common.notAvailable')} />
              <Field label={t('gateways.wba.columns.started')} value={formatDateTime(open.started_at)} />
              <Field
                label={t('gateways.wba.drawer.completed')}
                value={open.completed_at ? formatDateTime(open.completed_at) : t('gateways.wba.drawer.notCompleted')}
              />
            </Stack>

            {open.error_code && (
              <Alert color="error" title={open.error_code}>
                <Text role="bodySmall">{open.error_message ?? t('gateways.wba.drawer.noErrorMessage')}</Text>
              </Alert>
            )}

            <Stack gap="xs">
              <Text role="bodySmall" fw={600}>{t('gateways.wba.drawer.timeline')}</Text>
              {(open.transitions ?? []).length === 0 ? (
                <Text role="caption" c={MUTED}>{t('gateways.wba.drawer.noTransitions')}</Text>
              ) : (
                (open.transitions ?? []).map((tr, i) => (
                  <Group key={`${tr.at}-${i}`} justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Text role="bodySmall">{t(`gateways.wba.step.${tr.step}`)}</Text>
                      <Text role="caption" c={MUTED}>{tr.detail}</Text>
                    </Stack>
                    <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }} title={formatDateTime(tr.at)}>
                      {formatRelative(tr.at)}
                    </Text>
                  </Group>
                ))
              )}
            </Stack>

            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => nav(`/tenants/${open.organisation_id}`)}>
                {t('gateways.numbers.actions.openTenant')}
              </Button>
              <Button size="xs" variant="subtle" onClick={() => nav('/gateways/numbers')}>
                {t('gateways.wba.interim.openNumbers')}
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

function Field({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall" style={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Text>
    </Group>
  );
}
